import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Modal from "react-modal";
import "../assets/css/Modals.css";
import "../assets/css/OtaArtifacts.css";
import "react-toastify/dist/ReactToastify.css";
import {
  createThing,
  deleteThing,
  getAllDeviceDetails,
  getAllDeviceModels,
} from "../commonApi";
import { isOperatorOnly } from "../keycloakConfig";
import DeviceActionCell from "./DeviceActionCell";
import CopyableCellText from "./CopyableCellText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import moment from "moment";
import { Loader } from "../Loader";

const Devices = () => {
  const operatorOnly = isOperatorOnly();
  const [filteredDeviceData, setFilteredDeviceData] = useState([]);
  const [countPerPage, setCountPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [deleteThingName, setDeleteThingName] = useState(null);
  const [search, setSearch] = useState("");
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [addDeviceModalOpen, setAddDeviceModalOpen] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [deviceModels, setDeviceModels] = useState([]);
  const saveButtonRef = useRef(null);
  const expectedPageRef = useRef(0);

  const [newDevice, setNewDevice] = useState({
    modelId: "",
    description: "",
  });

  const [errors, setErrors] = useState({});

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
    expectedPageRef.current = 0;
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchDataAndDevices = async () => {
      setLoading(true);
      try {
        await fetchData();
        if (!isCancelled) {
          await loadDevices();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error("Failed to load data");
          console.error("Error loading data:", error);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchDataAndDevices();
    return () => {
      isCancelled = true;
    };
  }, [page, countPerPage, search, sortField, sortOrder]);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await getAllDeviceDetails({
        pageNo: page,
        pageSize: countPerPage,
        searchValue: search,
        sortField: sortField,
        sortOrder: sortOrder,
      });
      const devices = (response?.data?.content || []).filter(
        (device) => device && typeof device === "object" && device.thingName
      );
      setFilteredDeviceData(devices);
      setTotalRows(response?.data?.totalElements || 0);
      if (response?.data?.totalElements > 0 && devices.length === 0) {
        toast.warn(`No devices found for page ${page + 1}`);
      }
    } catch (error) {
      toast.error("Failed to load devices");
      console.error("Error loading devices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const handleDelete = (thingName) => {
    setDeleteThingName(thingName);
    setModalDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;

    setIsDeleteDisabled(true);

    try {
      await deleteThing(deleteThingName);
      toast.success("Device deleted successfully");
      setPage(0);
      expectedPageRef.current = 0;
      await loadDevices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete device");
    } finally {
      setIsDeleteDisabled(false);
      setModalDeleteOpen(false);
      setDeleteThingName(null);
    }
  };

  const handleAddDeviceChange = (e) => {
    const { name, value } = e.target;
    setNewDevice((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleAddDeviceSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!newDevice.modelId) {
      newErrors.modelId = "Device model is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (saveButtonRef.current) {
      saveButtonRef.current.disabled = true;
    }
    setIsSaveDisabled(true);

    try {
      await createThing({
        modelId: newDevice.modelId,
        description: newDevice.description,
      });
      toast.success("Device created successfully.");
      setPage(0);
      expectedPageRef.current = 0;
      await loadDevices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create device");
      console.error("Error creating device:", error);
    } finally {
      if (saveButtonRef.current) {
        saveButtonRef.current.disabled = false;
      }
      setIsSaveDisabled(false);
      setNewDevice({
        modelId: "",
        description: "",
      });
      setErrors({});
      setAddDeviceModalOpen(false);
    }
  };

  const fetchData = async () => {
    try {
      const response = await getAllDeviceModels();
      const models = response?.data?.data?.content || [];
      setDeviceModels(
        models.map((model) => ({
          id: model.id,
          name: model.name,
        }))
      );
    } catch (error) {
      toast.error("Failed to fetch device models");
      console.error("Error fetching device models:", error);
    }
  };

  const resetForm = () => {
    setNewDevice({
      modelId: "",
      description: "",
    });
    setErrors({});
  };

  return (
    <div className="custom_tab_wrapper">
      <style>
        {`
          .btn_red {
            background-color: #dc3545;
            border-color: #dc3545;
            color: white;
          }
          .btn_red:disabled {
            background-color: #f28b94;
            border-color: #f28b94;
            color: white;
            opacity: 0.8;
          }
          .btn_light__red {
            background-color: #f8d7da;
            border-color: #f5c6cb;
            color: #721c24;
          }
          .btn_light__red:disabled {
            background-color: #f8d7da;
            border-color: #f5c6cb;
            color: #721c24;
            opacity: 0.65;
          }
        `}
      </style>

      <div className="tab-pane fade show active" id="total-client-count" role="tabpanel">
        <ul className="nav nav-tabs" id="myTab">
          <li className="nav-item">
            <button
              className="nav-link active"
              data-bs-toggle="tab"
              data-bs-target="#home"
              type="button"
              role="tab"
            >
              Devices: {totalRows}
            </button>
          </li>
        </ul>

        <div className="tab-pane fade show active" id="home" role="tabpanel">
          <div className="tab_content_wrapper full_height_tab">
            <div className="custom_table_action">
              <div>
                <input
                  className="form-control"
                  type="text"
                  onChange={handleSearchInputChange}
                  placeholder="Search by Name"
                />
              </div>
              <div>
                <button
                  className="btn primary_btn text-center"
                  style={{ float: "right" }}
                  onClick={() => setAddDeviceModalOpen(true)}
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="Add New Device"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>
            <div className="table-responsive h-100vh-150px">
              {loading ? (
                <div className="spin-loader">
                  <Loader />
                </div>
              ) : (
                <div
                  className={`custom_table${!operatorOnly ? " customs-table" : ""}`}
                  style={operatorOnly ? { overflowX: "auto", width: "100%" } : undefined}
                >
                  <DataTable
                    columns={[
                      {
                        name: "User Given Name",
                        selector: (row) => <CopyableCellText value={row?.userAssignedDeviceName} />,
                        sortable: true,
                        sortField: "userAssignedDeviceName",
                        width: "285px",
                      },
                      {
                        name: "Device Name",
                        selector: (row) => <CopyableCellText value={row?.thingName} />,
                        sortable: true,
                        sortField: "thingName",
                        width: "285px",
                      },
                      {
                        name: "Firmware Version",
                        selector: (row) => row?.firmwareVersion || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        width: "180px",
                      },
                      {
                        name: "Hardware Version",
                        selector: (row) => row?.hardwareVersion || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        width: "180px",
                      },
                      {
                        name: "Device Group Name",
                        selector: (row) => row?.thingGroupName || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        sortable: true,
                        sortField: "thingGroupName",
                        width: "285px",
                      },
                      {
                        name: "Device Model",
                        selector: (row) => row?.modelName || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        sortable: true,
                        sortField: "modelName",
                        width: "285px",
                      },
                      {
                        name: 'Created Date',
                        selector: row => row.createdDate ? moment(row.createdDate).format('YYYY-MM-DD HH:mm:ss') : '-',
                        sortable: true,
                        sortField: "createdDate",
                        width: "300px",
                        cell: (row) => (
                          <div style={{ width: "100%" }}>
                            {row?.createdDate ? (
                              moment(row.createdDate).format("YYYY-MM-DD HH:mm:ss")
                            ) : (
                              <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>
                            )}
                          </div>
                        ),
                      },
                      ...(!operatorOnly
                        ? [
                            {
                              name: "Actions",
                              cell: (row) => (
                                <DeviceActionCell row={row} onDelete={handleDelete} />
                              ),
                            },
                          ]
                        : []),
                    ]}
                    data={filteredDeviceData}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={countPerPage}
                    paginationDefaultPage={page + 1}
                    onChangePage={(newPage) => {
                      console.log("Changing page:", { newPage, currentPage: page, expectedPage: expectedPageRef.current });
                      if (newPage - 1 !== expectedPageRef.current) {
                        console.warn("Unexpected page reset detected:", { newPage, expectedPage: expectedPageRef.current });
                      }
                      setPage(newPage - 1);
                      expectedPageRef.current = newPage - 1;
                    }}
                    onChangeRowsPerPage={(newPerPage) => {
                      console.log("Changing rows per page:", { newPerPage, currentPage: page });
                      setCountPerPage(newPerPage);
                      setPage(0);
                      expectedPageRef.current = 0;
                    }}
                    highlightOnHover
                    onSort={handleSort}
                    sortServer
                    fixedHeader={!addDeviceModalOpen}
                    fixedHeaderScrollHeight="440px"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {addDeviceModalOpen && (
          <Modal
            isOpen={addDeviceModalOpen}
            onRequestClose={() => setAddDeviceModalOpen(false)}
            contentLabel="Add Device Modal"
            style={{
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                width: "400px",
                padding: "24px",
                borderRadius: "12px",
                border: "1px solid #ddd",
                backgroundColor: "#fff",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.4)",
              },
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.6)",
              },
            }}
          >
            <div className="add-device-modal">
              <h3>Add New Device</h3>
              <form className="add-device-form" onSubmit={handleAddDeviceSubmit}>
                <label htmlFor="modelId">
                  Device Model:
                  <span className="required-asterisk" style={{ color: "red" }}>
                    *
                  </span>
                  <select
                    data-testid="modelId"
                    id="modelId"
                    name="modelId"
                    value={newDevice.modelId}
                    onChange={handleAddDeviceChange}
                    className="form-control"
                  >
                    <option value="">Select Device Model</option>
                    {deviceModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </label>
                {errors.modelId && (
                  <p className="error_msg" style={{ color: "red" }}>
                    {errors.modelId}
                  </p>
                )}

                <label htmlFor="description">
                  Description:
                  <textarea
                    id="description"
                    className="form-control"
                    name="description"
                    value={newDevice.description}
                    onChange={handleAddDeviceChange}
                  />
                </label>
                <div className="add-modal-footer">
                  <button
                    type="button"
                    className="btn light_primary_btn me-3"
                    onClick={() => {
                      resetForm();
                      setAddDeviceModalOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    data-testid="addbutton"
                    className="btn primary_btn btn-loader"
                    ref={saveButtonRef}
                    disabled={isSaveDisabled}
                  >
                    {isSaveDisabled ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            </div>
          </Modal>
        )}

        {modalDeleteOpen && (
          <div
            className="modal fade discard_modal show"
            id="delete-model"
            style={{ display: "block" }}
            aria-hidden={!modalDeleteOpen}
            aria-labelledby="exampleModalToggleLabel"
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <p className="modal-title" id="staticBackdropLabel">
                    Discard Changes
                  </p>
                </div>
                <div className="modal-body">
                  <p className="modal-msg-title">Delete</p>
                  <p className="modal-msg-desc">
                    Are you sure you want to Delete !!
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn_light__red mx-2"
                    data-bs-dismiss="modal"
                    onClick={() => setModalDeleteOpen(false)}
                    aria-label="Close"
                    disabled={isDeleteDisabled}
                  >
                    Not Now
                  </button>
                  <button
                    data-testid="deleteButton"
                    className="btn btn_red btn-loader"
                    onClick={() => confirmDelete()}
                    disabled={isDeleteDisabled}
                  >
                    {isDeleteDisabled ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Devices;